import React, { useState } from 'react'
import { Card, Button, TypoGraph, Toast } from 'components'
import { saveCompanyMonitoring } from 'api'
import { IRecipientCandidate } from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { isViewOnly } from 'tools/auth'
import RecipientsInput from '../recipientsInput'
import styles from '../fleetMonitoring.module.scss'

const FM = strings.FLEET_MONITORING

interface IOnboardingViewProps {
  onEnrolled: (opts?: { skipped?: boolean }) => void
  candidates: IRecipientCandidate[]
}

const OnboardingView: React.FunctionComponent<IOnboardingViewProps> = ({
  onEnrolled,
  candidates,
}) => {
  const [step, setStep] = useState<'intro' | 'configure'>('intro')
  const [recipients, setRecipients] = useState<string[]>([])
  const [distanceUnit, setDistanceUnit] = useState<'miles' | 'kilometers'>('miles')
  const [submitting, setSubmitting] = useState(false)
  const readOnly = isViewOnly()

  const handleEnroll = async (opts?: { skipped?: boolean }) => {
    const finalRecipients = opts?.skipped ? [] : recipients
    setSubmitting(true)
    try {
      await saveCompanyMonitoring({
        active: !opts?.skipped,
        defaultRecipients: finalRecipients,
        distanceUnit: opts?.skipped ? 'miles' : distanceUnit,
        validationTypes: [],
      })
      Toast({ type: 'success', subTitle: FM.TOASTS.ENROLLED })
      onEnrolled({ skipped: !!opts?.skipped })
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.ENROLL_FAILED })
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'intro') {
    return (
      <div className={styles.onboardingWrap}>
        <Card>
          <div className={styles.hero}>
            <div className={styles.heroIcon} aria-hidden="true">📡</div>
            <TypoGraph
              variant="h1"
              content={FM.ONBOARDING.INTRO_TITLE}
              className={styles.heroTitle}
            />
            <TypoGraph
              variant="subtitle1"
              content={FM.ONBOARDING.INTRO_SUBTITLE}
              className={styles.heroSubtitle}
            />
            <TypoGraph
              variant="body1"
              content={FM.ONBOARDING.INTRO_BODY}
              className={styles.heroBody}
            />
            <div className={styles.heroActions}>
              <Button
                label={FM.ONBOARDING.SKIP_SETUP}
                variant="outlined"
                onClick={() => handleEnroll({ skipped: true })}
                disabled={submitting || readOnly}
              />
              <Button
                label={FM.ONBOARDING.CONFIGURE_NOW}
                variant="contained"
                onClick={() => setStep('configure')}
                disabled={submitting || readOnly}
              />
            </div>
            <TypoGraph
              variant="caption"
              content={FM.ONBOARDING.INTRO_NOTE}
              className={styles.heroNote}
            />
          </div>
        </Card>
      </div>
    )
  }

  const canEnroll = recipients.length > 0 && !submitting

  return (
    <div className={styles.onboardingWrap}>
      <Card>
        <div className={styles.configureForm}>
          <div className={styles.configureHeader}>
            <TypoGraph
              variant="h2"
              content={FM.ONBOARDING.CONFIGURE_TITLE}
              className={styles.configureTitle}
            />
            <TypoGraph
              variant="subtitle1"
              content={FM.ONBOARDING.CONFIGURE_SUBTITLE}
            />
          </div>

          <section>
            <TypoGraph
              variant="caption"
              content={FM.ONBOARDING.DEFAULT_RECIPIENTS_LABEL}
              className={styles.sectionHeading}
            />
            <TypoGraph
              variant="body2"
              content={FM.ONBOARDING.DEFAULT_RECIPIENTS_HELPER}
              className={styles.sectionHelper}
            />
            <RecipientsInput
              value={recipients}
              onChange={setRecipients}
              candidates={candidates}
              disabled={readOnly}
            />
          </section>

          <section>
            <TypoGraph
              variant="caption"
              content={FM.ONBOARDING.DISTANCE_UNIT_LABEL}
              className={styles.sectionHeading}
            />
            <TypoGraph
              variant="body2"
              content={FM.ONBOARDING.DISTANCE_UNIT_HELPER}
              className={styles.sectionHelper}
            />
            <div className={styles.unitToggle}>
              <button
                type="button"
                disabled={readOnly}
                className={`${styles.unitButton} ${distanceUnit === 'miles' ? styles.unitButtonActive : ''}`}
                onClick={() => setDistanceUnit('miles')}
              >
                {FM.COMMON.MILES}
              </button>
              <button
                type="button"
                disabled={readOnly}
                className={`${styles.unitButton} ${distanceUnit === 'kilometers' ? styles.unitButtonActive : ''}`}
                onClick={() => setDistanceUnit('kilometers')}
              >
                {FM.COMMON.KILOMETERS}
              </button>
            </div>
          </section>

          <div className={styles.formFooter}>
            <Button label={FM.COMMON.BACK} variant="text" onClick={() => setStep('intro')} />
            <Button
              label={FM.ONBOARDING.ENROLL}
              variant="contained"
              onClick={() => handleEnroll()}
              disabled={!canEnroll || readOnly}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}

export default OnboardingView
